import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { getOwner } from '@ember/application';
import * as Sentry from '@sentry/ember';
import getConfig from '../lib/get-config.js';
import { supportsCssLayers, installCssLayerFallback } from '../lib/css-layer-fallback.js';

var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
const UI_STORAGE_KEY = 'beebee_ui';
const UI_CLASSICA = 'classica';
const UI_NUOVA = 'nuova';
const UI_VERSIONS = [UI_CLASSICA, UI_NUOVA];
// Production preview: the tenants (organization slug) that see the switch before the release.
const UI_PREVIEW_ORGS = ['fisiotest', 'poppixsrl'];
// Set on a device by `?ui=new`: the preview is unlocked there for any tenant.
const UI_UNLOCK_KEY = 'beebee_ui_unlock';
function readUnlocked() {
  try {
    return window.localStorage?.getItem(UI_UNLOCK_KEY) === '1';
  } catch {
    return false;
  }
}
function writeUnlocked(on) {
  try {
    if (on) {
      window.localStorage?.setItem(UI_UNLOCK_KEY, '1');
    } else {
      window.localStorage?.removeItem(UI_UNLOCK_KEY);
    }
  } catch {
    // storage blocked: the unlock still holds for this page load
  }
}

// `?ui=new` / `?ui=old` (also nuova / classica / classic); anything else is ignored.
function readQueryVersion() {
  try {
    const value = new URLSearchParams(window.location.search).get('ui');
    if (value === 'new' || value === UI_NUOVA) return UI_NUOVA;
    if (value === 'old' || value === 'classic' || value === UI_CLASSICA) return UI_CLASSICA;
  } catch {
    // no location (tests, SSR): no query switch
  }
  return null;
}
function readStoredVersion() {
  // Reading storage can itself throw (blocked site data, a document that is
  // already unloading): the switch must still boot, on the classic default.
  try {
    const stored = window.localStorage?.getItem(UI_STORAGE_KEY);
    return UI_VERSIONS.includes(stored) ? stored : UI_CLASSICA;
  } catch {
    return UI_CLASSICA;
  }
}
function tagSentry(version) {
  // Every error report says which UI the user was on. Sentry.setTag is a
  // no-op when the SDK was never initialised (tests, dev); the guard only
  // covers a build where the import resolved to something unexpected.
  try {
    if (typeof Sentry?.setTag === 'function') {
      Sentry.setTag('ui', version);
    }
  } catch {
    // reporting is best effort, never a reason to break the switch
  }
}
let UiService = (_class = class UiService extends Service {
  constructor() {
    super(...arguments);
    _initializerDefineProperty(this, "version", _descriptor, this);
    // Per-app opt-in, flipped by the app's own instance-initializer (medical
    // first). See the MAP gotcha: without it the stored choice is ignored.
    _initializerDefineProperty(this, "enabled", _descriptor2, this);
    // `?ui=new` was used on this device (see the MAP gotcha «PREVIEW GATE»).
    _initializerDefineProperty(this, "unlocked", _descriptor3, this);
    // Tenant slug pushed by session.setMe() (me.organization.organization). Never looked up from here: an eager
    // service:session lookup at enable() instantiated the real session before the test mocks and its HTTPS
    // bootstrap redirect killed the test runner page.
    _initializerDefineProperty(this, "tenant", _descriptor4, this);
    this.version = readStoredVersion();
    this.unlocked = readUnlocked();
  }
  get environment() {
    try {
      return getConfig(this)?.environment ?? 'production';
    } catch {
      return 'production';
    }
  }
  /** Called by session.setMe() on login / logout; re-applies the gate. */
  setTenant(slug) {
    this.tenant = String(slug ?? '').toLowerCase();
    this.apply();
  }

  /** The switch is shown: development, a preview tenant, or an unlocked device. */
  get canChoose() {
    return this.enabled &&
    // the new UI needs cascade layers (classic is isolated in `@layer classica`): never offered without them
    supportsCssLayers() && (this.environment === 'development' || this.unlocked || UI_PREVIEW_ORGS.includes(this.tenant));
  }

  /** The stored 'nuova' may apply: wherever the switch shows, and in every non-production build. */
  get canUse() {
    return this.canChoose || this.enabled && supportsCssLayers() && this.environment !== 'production';
  }
  get isNuova() {
    return this.canUse && this.version === UI_NUOVA;
  }

  /** Opt this app in: the stored choice (or a `?ui=` in the URL) now applies. Idempotent. */
  enable() {
    this.enabled = true;
    // browsers without cascade layers drop the whole layered classic stylesheet: re-serve it unwrapped
    installCssLayerFallback();
    const fromQuery = readQueryVersion();
    if (fromQuery) {
      this.unlocked = fromQuery === UI_NUOVA;
      writeUnlocked(this.unlocked);
      this.setVersion(fromQuery);
      return;
    }
    this.apply();
  }

  /**
   * @param {string} value 'classica' | 'nuova'
   * @returns {boolean} false (and no change) for an unknown value
   */
  setVersion(value) {
    if (!UI_VERSIONS.includes(value)) {
      return false;
    }
    this.version = value;
    try {
      window.localStorage?.setItem(UI_STORAGE_KEY, value);
    } catch {
      // storage full/blocked: the choice still applies for this session
    }
    this.apply();
    return true;
  }
  apply() {
    const html = document.documentElement;
    if (this.isNuova) {
      html.dataset.ui = UI_NUOVA;
    } else {
      delete html.dataset.ui;
    }
    if (this.enabled) {
      tagSentry(this.isNuova ? UI_NUOVA : UI_CLASSICA);
    }
  }

  /**
   * Dirty-form guard for the switch. Changing UI re-mounts the forked
   * templates: dirty attributes survive (they live on the record), but
   * component-local state is dropped and ember-concurrency cancels any task
   * of the unmounted component at its next yield. There is no app-wide
   * leave-guard in the shell, so this reuses the same predicate the modal
   * shell uses before closing (`item.hasDirtyAttributes`, bb-modal.js
   * closeModal) on every record the shell is showing:
   *   - every edit modal on the modal stack,
   *   - the model of each route in the current route chain (view pages),
   * plus a save still in flight on the application controller.
   * @returns {boolean}
   */
  hasUnsavedChanges() {
    const owner = getOwner(this);
    if (!owner) {
      return false;
    }
    const stack = owner.lookup('service:modal-stack')?.modalComponents ?? [];
    if (stack.some(descriptor => descriptor?.item?.hasDirtyAttributes)) {
      return true;
    }

    // `_savingCount` is the counter behind _startSaving/_stopSaving (the
    // `beebee-saving` body class) — the only shell-wide in-flight-save signal.
    const applicationController = owner.lookup('controller:application');
    if ((applicationController?._savingCount ?? 0) > 0) {
      return true;
    }
    let routeInfo = owner.lookup('service:router')?.currentRoute ?? null;
    while (routeInfo) {
      if (routeInfo.attributes?.hasDirtyAttributes) {
        return true;
      }
      routeInfo = routeInfo.parent;
    }
    return false;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "version", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return UI_CLASSICA;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "enabled", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "unlocked", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "tenant", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _class);

export { UI_CLASSICA, UI_NUOVA, UI_PREVIEW_ORGS, UI_STORAGE_KEY, UI_UNLOCK_KEY, UI_VERSIONS, UiService as default };
//# sourceMappingURL=ui.js.map
